import { useEffect, useRef, useState } from 'react';

/**
 * Which way is the buggy pointing?
 *
 * Two sources, and neither is sufficient alone:
 *
 *   COMPASS (magnetometer). Works standing still, which is most of golf — you
 *   park, get out, look at the green. This is the one that answers "the
 *   direction you're looking". Needs an explicit permission prompt on iOS, and
 *   that prompt must be triggered by a tap.
 *
 *   GPS COURSE. Only meaningful while actually moving; a stationary receiver
 *   reports noise or nothing. But it is always available, needs no permission,
 *   and is more reliable than a magnetometer sitting next to a buggy's electric
 *   motor and steel frame.
 *
 * So: use the compass when we have it and the buggy is stopped, use GPS course
 * once moving, and never snap back to a stale value in between.
 */

interface OrientationEventish extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

type PermissionState = 'unsupported' | 'prompt' | 'granted' | 'denied';

/** iOS 13+ gates the magnetometer behind a user-gesture permission call. */
function needsPermission(): boolean {
  const D = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> };
  return typeof D?.requestPermission === 'function';
}

export function useDeviceHeading() {
  const [heading, setHeading] = useState<number | null>(null);
  const [permission, setPermission] = useState<PermissionState>(() =>
    typeof window === 'undefined' || !('DeviceOrientationEvent' in window)
      ? 'unsupported'
      : needsPermission()
        ? 'prompt'
        : 'granted'
  );
  const listening = useRef(false);

  const attach = () => {
    if (listening.current) return;
    listening.current = true;

    const onOrient = (raw: Event) => {
      const e = raw as OrientationEventish;
      // iOS reports a true-north compass heading directly. Everywhere else,
      // absolute alpha is measured anticlockwise from north, so it has to be
      // inverted to give a clockwise bearing.
      if (typeof e.webkitCompassHeading === 'number' && !Number.isNaN(e.webkitCompassHeading)) {
        setHeading((e.webkitCompassHeading + 360) % 360);
        return;
      }
      if (e.absolute && typeof e.alpha === 'number') {
        setHeading((360 - e.alpha) % 360);
      }
    };

    window.addEventListener('deviceorientationabsolute', onOrient as EventListener, true);
    window.addEventListener('deviceorientation', onOrient as EventListener, true);
  };

  useEffect(() => {
    if (permission === 'granted') attach();
    return () => {
      /* listeners are cheap and live for the session; nothing to tear down */
    };
  }, [permission]);

  /** Must be called from a tap — iOS rejects it otherwise. */
  const request = async () => {
    if (!needsPermission()) {
      setPermission('granted');
      attach();
      return;
    }
    try {
      const D = DeviceOrientationEvent as unknown as { requestPermission: () => Promise<string> };
      const res = await D.requestPermission();
      setPermission(res === 'granted' ? 'granted' : 'denied');
      if (res === 'granted') attach();
    } catch {
      setPermission('denied');
    }
  };

  return { heading, permission, request };
}

/** Below this the GPS course reading is noise, not direction. */
const MOVING_MS = 0.7;

/**
 * The heading to draw the buggy at.
 *
 * Prefers GPS course once genuinely moving — a magnetometer inside a steel
 * buggy with an electric motor under the seat is not to be trusted at speed.
 * Falls back to the compass when stopped, and holds the last good value rather
 * than spinning to zero when both go quiet.
 */
export function resolveHeading(opts: {
  gpsHeading: number | null;
  speed: number;
  compassHeading: number | null;
  previous: number | null;
}): number | null {
  const { gpsHeading, speed, compassHeading, previous } = opts;
  if (speed >= MOVING_MS && gpsHeading != null && !Number.isNaN(gpsHeading)) return gpsHeading;
  if (compassHeading != null && !Number.isNaN(compassHeading)) return compassHeading;
  if (gpsHeading != null && !Number.isNaN(gpsHeading)) return gpsHeading;
  return previous;
}
